import { useEffect, useRef, useState } from "react";

import useInstallTemplate from "./useInstallTemplate";
import useRequestTemplateAccess from "./useRequestTemplateAccess";

function useAsyncCall() {
  const mounted = useRef(false);
  const [isRunning, setRunning] = useState(null);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const makeCall = (id) => {
    return (action) => {
      return async (args) => {
        if (mounted.current) {
          setRunning(id);
          await action(args);
          if (mounted.current) {
            setRunning(null);
          }
        }
      };
    };
  };

  return [makeCall, isRunning];
}

export function useTemplateAction(onDone = () => {}) {
  const [makeCall, running] = useAsyncCall();
  const { install, installing } = useInstallTemplate(onDone);
  const { request, requesting } = useRequestTemplateAccess((data, template) => {
    template.installableStatus = data.updatedStatus;
  });

  const map = (t) => {
    const call = makeCall(t.id);

    return {
      ...t,
      installing: running === t.id,
      install: call(async () => {
        await install(
          { releaseId: t.current.id },
          {
            successMsg: `${t.current.title} was successfully added to your Hub`,
            defaultErrorMsg: `Failed to install ${t.current.title}`,
          }
        );
      }),
      request: call(async () => {
        await request(
          { templateReleaseId: t.current.uuid },
          {
            successMsg: `${t.current.title} was successfully requested`,
            defaultErrorMsg: `Failed to request ${t.current.title}`,
            context: t,
          }
        );
      }),
    };
  };

  return [map, installing || requesting];
}

export default useTemplateAction;
